/**
 * Shared per-gun SKU lookup for the header search (snippets/gun-search.liquid) and the
 * results page (assets/custom-grid-engine.js).
 *
 * Cloak, Low-Pro and Rapid Force are single-variant products in Shopify; their real SKUs live in
 * the fitment data files listed in snippets/sku-search-sources.liquid. assets/sku-index.json is a
 * compact prebuilt copy of those files (scripts/sync-sku-index.sh). Any data file that changed after
 * the index was published, or isn't in it yet, is loaded directly, so search never goes out of sync.
 */
window.AGHSkuSearch = (function () {
  'use strict';

  const config = window.AGH_SKU_SEARCH || { indexUrl: null, sources: [] };
  let loadPromise = null;
  let rows = []; // { sku, skuLower, handle, title, manufacturer, gun, hand, light }

  // Shopify asset URLs end in ?v=<checksum><10-digit unix timestamp of the last update>
  function updatedAt(url) {
    const match = /[?&]v=(\d{11,})/.exec(url || '');
    return match ? Number(match[1].slice(-10)) : null;
  }

  // asset_url gives protocol-relative URLs (//store/cdn/...). On an http page (shopify theme dev) that
  // becomes an http request, which Shopify redirects to https without CORS headers, so Firefox blocks it.
  function httpsUrl(url) {
    return url && url.startsWith('//') ? 'https:' + url : url;
  }

  function cleanValue(value) {
    value = value == null ? '' : String(value).trim();
    return ['', 'none', 'null', 'false'].includes(value.toLowerCase()) ? '' : value;
  }

  function handLabel(value) {
    const first = cleanValue(value).charAt(0).toUpperCase();
    return first === 'L' ? 'Left' : first === 'R' ? 'Right' : '';
  }

  // Mirrors scripts/build-sku-index.py: one row per SKU, light dropped if the SKU spans several lights
  function rowsFromVariants(variants) {
    const bySku = new Map();
    (variants || []).forEach(v => {
      const sku = cleanValue(v.sku);
      if (!sku || !v.manufacturer || !v.gun) return;
      const row = [sku, cleanValue(v.manufacturer), cleanValue(v.gun), handLabel(v.handedness).charAt(0), cleanValue(v.light)];
      const existing = bySku.get(sku);
      if (!existing) bySku.set(sku, row);
      else if (existing[4] !== row[4]) existing[4] = '';
    });
    return Array.from(bySku.values());
  }

  function addRows(source, sourceRows) {
    sourceRows.forEach(([sku, manufacturer, gun, hand, light]) => {
      rows.push({
        sku,
        skuLower: sku.toLowerCase(),
        handle: source.handle,
        title: source.title,
        manufacturer,
        gun,
        hand: handLabel(hand),
        light
      });
    });
  }

  function load() {
    if (loadPromise) return loadPromise;

    const indexPromise = config.indexUrl
      ? fetch(httpsUrl(config.indexUrl)).then(r => (r.ok ? r.json() : null)).catch(() => null)
      : Promise.resolve(null);

    loadPromise = indexPromise.then(index => {
      const indexUpdatedAt = updatedAt(config.indexUrl);
      const indexed = new Map();
      if (index && Array.isArray(index.sources)) {
        index.sources.forEach(entry => indexed.set(entry.file, entry.rows));
      }

      return Promise.all(config.sources.map(source => {
        const sourceUpdatedAt = updatedAt(source.url);
        const isStale = !indexed.has(source.file) ||
          (sourceUpdatedAt && indexUpdatedAt && sourceUpdatedAt > indexUpdatedAt);

        if (!isStale) {
          addRows(source, indexed.get(source.file));
          return null;
        }

        console.warn(`[SKU search] ${source.file} is newer than sku-index.json; loading it directly. Run scripts/sync-sku-index.sh to update the index.`);
        return fetch(httpsUrl(source.url))
          .then(r => r.json())
          .then(data => addRows(source, rowsFromVariants(data.variants)))
          .catch(e => {
            console.error(`[SKU search] Failed to load ${source.file}:`, e);
            // Fall back to the older indexed copy rather than dropping the product
            if (indexed.has(source.file)) addRows(source, indexed.get(source.file));
          });
      }));
    }).then(() => rows.length);

    return loadPromise;
  }

  // SKUs containing every search term, exact match first. Terms must be lowercase.
  function match(searchTerms, limit) {
    if (!searchTerms.length || !searchTerms.some(t => t.length >= 3)) return { items: [], total: 0 };
    const exact = searchTerms.join(' ');
    const hits = rows.filter(row => searchTerms.every(t => row.skuLower.includes(t)));
    hits.sort((a, b) => (b.skuLower === exact) - (a.skuLower === exact) || a.sku.localeCompare(b.sku));
    return { items: limit ? hits.slice(0, limit) : hits, total: hits.length };
  }

  return { load, match };
})();
